import React, { useRef } from 'react';
import { DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Switch, NumberInput } from '@/components/ui';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import {
  TIMELINE_AXIS_LABEL_CLASS,
  TIMELINE_AXIS_TICK_CLASS,
  TIMELINE_NOTE_TEXT_CLASS,
} from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';
import { ControlRow } from '@/components/config_forms/shared/ControlRow';

const AXIS_EMPHASIS_COLOR = 'var(--primary-text)';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface MealDeadlineTimelineProps {
  deadlineHours: number;
  gracePeriodMinutes: number;
  multipleBreaks: boolean;
  onDeadlineChange?: (value: number | null) => void;
  onMultipleBreaksChange?: (value: boolean) => void;
  onGracePeriodChange?: (value: number | null) => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatHoursMinutes(totalHours: number): string {
  const totalMinutes = Math.round(totalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

const SurchargeNote: React.FC = () => (
  <div className={`mt-1 mb-3 px-2 py-2 rounded-[0.25rem] border border-destructive/10 bg-destructive/[0.06] ${TIMELINE_NOTE_TEXT_CLASS}`}>
    <div className="flex items-start gap-2">
      <DollarSign className="h-3.5 w-3.5 shrink-0 mt-1 text-destructive-text/85" aria-hidden="true" />
      <span>
        <strong className="text-destructive-text/85 font-medium">Grace Period:</strong>{' '}
        The grace period only delays when the surcharge <em>triggers</em> — it doesn't shift when it <em>starts</em>.
      </span>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  ZoneBar                                                            */
/* ------------------------------------------------------------------ */

const ZoneBar: React.FC<{
  deadlinePct: number;
  gracePct: number;
}> = ({ deadlinePct, gracePct }) => {
  const graceBandPct = gracePct - deadlinePct;
  const surchargeStartPct = gracePct;
  const surchargeWidthPct = 100 - surchargeStartPct;

  return (
    <div
      className="relative w-full h-14 rounded-[0.25rem] overflow-hidden"
      style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--border), transparent 40%)' }}
    >
      {/* Safe zone */}
      <div
        className="absolute inset-y-0 left-0 bg-scenario-green/8 border-r-2 border-r-scenario-green/30 flex items-center justify-center"
        style={{ width: `${deadlinePct}%` }}
      >
        <span className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.5px] pointer-events-none" style={{ color: 'color-mix(in srgb, var(--color-scenario-green), white 20%)' }}>
          Safe
        </span>
      </div>
      {/* Grace zone — always rendered, animated via width */}
      <div
        className={cn(
          'absolute inset-y-0 flex items-center justify-center',
          'motion-safe:transition-[width] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]',
          graceBandPct > 0 && 'bg-primary/[0.06] border-r border-dashed border-r-destructive/25',
        )}
        style={{ left: `${deadlinePct}%`, width: `${graceBandPct}%` }}
      >
        {graceBandPct > 6 && (
          <span className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.5px] pointer-events-none" style={{ color: 'var(--primary-text)' }}>
            Grace
          </span>
        )}
      </div>
      {/* Surcharge zone */}
      <div
        className="absolute inset-y-0 bg-destructive/[0.08] flex items-center justify-center motion-safe:transition-[left,width] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{ left: `${surchargeStartPct}%`, width: `${surchargeWidthPct}%` }}
      >
        <span className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.5px] pointer-events-none" style={{ color: 'var(--destructive-text)' }}>
          Surcharge
        </span>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Segment                                                            */
/* ------------------------------------------------------------------ */

const Segment: React.FC<{
  deadlineHours: number;
  gracePeriodMinutes: number;
  segmentOffset: number;
  opacity?: number;
  label?: string;
}> = ({
  deadlineHours,
  gracePeriodMinutes,
  segmentOffset,
  opacity = 1,
  label,
}) => {
  // Persist last label for smooth collapse (text stays during exit)
  const lastLabel = useRef(label);
  if (label) lastLabel.current = label;

  // Fixed layout positions — only label text is dynamic.
  // Balanced for 12px labels on 375px mobile: Safe needs least room (4 chars),
  // Grace and Surcharge need more (5 and 9 chars).
  const deadlinePct = 45;
  const graceBandPct = gracePeriodMinutes > 0 ? 20 : 0;
  const gracePct = deadlinePct + graceBandPct;

  return (
    <div
      className={cn(
        'motion-safe:transition-opacity motion-safe:duration-300',
        opacity < 1 && 'pointer-events-none',
      )}
      style={{ opacity }}
    >
      {/* Segment label */}
      <AnimatedCollapse open={!!label} duration={200}>
        <div className="flex items-center gap-2 mb-2">
          <span className="font-heading text-[11px] sm:text-[13px] leading-none uppercase tracking-[1.25px] text-foreground/90 font-semibold">
            {lastLabel.current}
          </span>
        </div>
      </AnimatedCollapse>

      {/* Zone bar */}
      <ZoneBar deadlinePct={deadlinePct} gracePct={gracePct} />

      {/* Time axis labels (all below timeline) */}
      <div className="relative h-[32px] mt-2 px-1 font-mono text-[10px] sm:text-[12px] leading-[1.15]">
        <span className={cn('absolute left-0 whitespace-pre', TIMELINE_AXIS_LABEL_CLASS)}>
          {segmentOffset === 0 ? 'Start' : 'Break End'}
        </span>
        <span
          className={`absolute whitespace-pre text-center font-normal tabular-nums -translate-x-1/2 ${TIMELINE_AXIS_TICK_CLASS}`}
          style={{ left: `${deadlinePct}%`, color: AXIS_EMPHASIS_COLOR }}
        >
          {`${formatHoursMinutes(deadlineHours)}\ndeadline`}
        </span>
        {gracePeriodMinutes > 0 && (
          <span
            className={`absolute whitespace-pre text-center font-normal tabular-nums -translate-x-1/2 motion-safe:transition-opacity motion-safe:duration-200 ${TIMELINE_AXIS_TICK_CLASS}`}
            style={{ left: `${gracePct}%`, color: AXIS_EMPHASIS_COLOR }}
          >
            {`${gracePeriodMinutes}m\ngrace`}
          </span>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  MealDeadlineTimeline (exported)                                    */
/* ------------------------------------------------------------------ */

export const MealDeadlineTimeline: React.FC<MealDeadlineTimelineProps> = ({
  deadlineHours,
  gracePeriodMinutes,
  multipleBreaks,
  onDeadlineChange,
  onMultipleBreaksChange,
  onGracePeriodChange,
}) => {
  const showSeg2 = multipleBreaks;

  return (
    <div role="group" aria-label="Meal deadline timeline">
      {/* Segment 1 */}
      <Segment
        deadlineHours={deadlineHours}
        gracePeriodMinutes={gracePeriodMinutes}
        segmentOffset={0}
        label={showSeg2 ? 'Work Segment 1' : undefined}
      />

      {/* Segment 2 (conditional) */}
      <AnimatedCollapse open={showSeg2} duration={300}>
        {/* Divider */}
        <div className="flex items-center gap-2 mt-1 mb-2">
          <div className="flex-1 border-t border-dashed border-primary/40" />
          <span className="font-heading text-[11px] sm:text-[13px] leading-none uppercase tracking-[1px] shrink-0 text-primary-text/90">
            Deadline Resets at Each Break End
          </span>
          <div className="flex-1 border-t border-dashed border-primary/40" />
        </div>

        <Segment
          deadlineHours={deadlineHours}
          gracePeriodMinutes={gracePeriodMinutes}
          segmentOffset={1}
          label="Work Segment 2"
        />

        {/* Continuation indicator */}
        <div className="flex items-center gap-2 mt-1 mb-2">
          <div className="flex-1 border-t border-dashed border-primary/40" />
          <span className="font-heading text-[11px] sm:text-[13px] leading-none uppercase tracking-[1px] text-primary-text/90">
            Additional segments as needed
          </span>
          <div className="flex-1 border-t border-dashed border-primary/40" />
        </div>
      </AnimatedCollapse>

      <SurchargeNote />

      {/* Controls — outside the bordered panel for full width */}
      <div className="rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
        {/* Meal break deadline input */}
        {onDeadlineChange && (
          <ControlRow label="Meal Break Deadline" description="The maximum hours of continuous work allowed before a meal break is required.">
            <NumberInput
              aria-label="Meal break deadline"
              unit="hrs"
              size="xs"
              touchTarget
              value={deadlineHours || ''}
              onChange={(e) => {
                const v = e.target.value;
                onDeadlineChange(v === '' ? null : Math.max(0, parseFloat(v) || 0));
              }}
            />
          </ControlRow>
        )}

        {/* Grace period input */}
        {onGracePeriodChange && (
          <ControlRow label="Grace Period" description="A buffer time that prevents surcharges for minor delays. Only missed breaks that exceed this grace period will trigger a surcharge.">
            <NumberInput
              aria-label="Grace period"
              unit="min"
              size="xs"
              touchTarget
              value={gracePeriodMinutes || ''}
              onChange={(e) => {
                const v = e.target.value;
                onGracePeriodChange(v === '' ? null : Math.max(0, parseInt(v, 10) || 0));
              }}
            />
          </ControlRow>
        )}

        {/* Recurring deadline toggle */}
        {onMultipleBreaksChange && (
          <ControlRow
            label="Recurring Deadline"
            description={multipleBreaks
              ? 'Each qualifying break resets the deadline. Longer shifts may require multiple breaks, and each missed deadline triggers its own surcharge.'
              : 'Only one meal break deadline is imposed per shift. After the first qualifying break or surcharge, monitoring ends.'}
          >
            <Switch
              checked={multipleBreaks}
              onCheckedChange={onMultipleBreaksChange}
              aria-label="Toggle multiple breaks"
            />
          </ControlRow>
        )}
      </div>
    </div>
  );
};
